import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const Navbar = ({ currentSection, totalSections }) => {
  const location = useLocation();
  const pathname = location.pathname === '/' ? '/' : location.pathname.replace(/\/+$/, '');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const navRef = useRef(null);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (navRef.current) {
      navRef.current.setAttribute('data-section', currentSection);
    }
  }, [currentSection]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(prevState => !prevState);
  };

  const handleNavClick = () => {
    setIsMenuOpen(false);

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'auto' });

      const scrollContainer = document.querySelector('.scroll-container');
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: 0, behavior: 'auto' });
      }

      const allScrollContainers = document.querySelectorAll('[class*="scroll"], [class*="overflow-"]');
      allScrollContainers.forEach(container => {
        if (container.scrollTop > 0) {
          container.scrollTo({ top: 0, behavior: 'auto' });
        }
      });
    }, 10);
  };

  const isTransparent = currentSection === 0;

  const navbarStyle = {
    backgroundColor: isTransparent ? 'transparent' : '#000000',
    padding: isTransparent ? '0.75rem 0' : '0.5rem 0'
  };

  const separatorStyle = {
    background:
      "linear-gradient(90deg, rgba(255,255,255,0.16) 0%, rgba(34,211,211,0.14) 24%, rgba(255,255,255,0.08) 58%, rgba(255,255,255,0) 100%)",
    opacity: isTransparent ? 0.58 : 0.76,
  };

  return (
    <motion.nav
      ref={navRef}
      style={navbarStyle}
      className="transition-all duration-800 ease-out navbar box-sizing-border-box"
      data-transparent={isTransparent ? "true" : "false"}
      data-section={currentSection}
    >
      <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 xl:px-12 flex justify-between items-center box-sizing-border-box">
        <div className="flex items-center">
          <Link to="/" className="flex items-center relative group" onClick={handleNavClick}>
            <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-[#22D3D3]/10 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <img
              src="/logo.webp"
              alt="SimplyfAI Logo"
              className="h-10 md:h-10 lg:h-14 w-auto relative z-10"
            />
          </Link>
        </div>

          <div className="hidden md:flex space-x-6 lg:space-x-8">
            <Link
              to="/"
              className={`transition text-[10px] lg:text-xs font-bold relative no-underline whitespace-nowrap ${
                pathname === '/' || pathname === ''
                  ? 'text-[#22D3D3] after:absolute after:bottom-0 after:left-0 after:h-0.5 after:bg-[#22D3D3] after:transition-all after:duration-300 after:w-full'
                  : 'text-white'
              }`}
              onClick={handleNavClick}
            >
              HOME
            </Link>
            <Link
              to="/about/"
              className={`transition text-[10px] lg:text-xs tracking-wide font-bold relative no-underline whitespace-nowrap ${
                pathname === '/about'
                  ? 'text-[#22D3D3] after:absolute after:bottom-0 after:left-0 after:h-0.5 after:bg-[#22D3D3] after:transition-all after:duration-300 after:w-full'
                  : 'text-white'
              }`}
              onClick={handleNavClick}
            >
              ABOUT
            </Link>
            <Link
              to="/evals/"
              className={`transition text-[10px] lg:text-xs tracking-wide font-bold relative no-underline whitespace-nowrap ${
                pathname === '/evals'
                  ? 'text-[#22D3D3] after:absolute after:bottom-0 after:left-0 after:h-0.5 after:bg-[#22D3D3] after:transition-all after:duration-300 after:w-full'
                  : 'text-white'
              }`}
              onClick={handleNavClick}
            >
              EVALS
            </Link>
            <Link
              to="/contact/"
              className={`transition text-[10px] lg:text-xs tracking-wide font-bold relative no-underline whitespace-nowrap ${
                pathname === '/contact'
                  ? 'text-[#22D3D3] after:absolute after:bottom-0 after:left-0 after:h-0.5 after:bg-[#22D3D3] after:transition-all after:duration-300 after:w-full'
                  : 'text-white'
              }`}
              onClick={handleNavClick}
            >
              CONTACT
            </Link>
            <a
              href="https://blog.simplyfai.io/"
              className="transition text-[10px] lg:text-xs tracking-wide font-bold relative no-underline whitespace-nowrap text-white"
              onClick={handleNavClick}
            >
              INSIGHTS
            </a>
          </div>

          <button
            ref={menuButtonRef}
            className={`md:hidden p-2 touch-manipulation bg-transparent border-none ${isMenuOpen ? 'z-[100]' : 'z-20'}`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <svg
              className="w-6 h-6 menu-icon"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                stroke="#22D3D3"
              />
            </svg>
          </button>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="md:hidden fixed inset-0 bg-black/95 backdrop-blur-sm z-[99]"
              initial={{ opacity: 0, y: "-100%" }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: "-100%" }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex flex-col items-center justify-center h-full space-y-8 text-center pt-16">
                <Link
                  to="/"
                  className={`transition text-base tracking-wide font-bold ${
                    pathname === '/' || pathname === ''
                      ? 'text-[#22D3D3]'
                      : 'text-white'
                  }`}
                  onClick={handleNavClick}
                >
                  HOME
                </Link>
                <Link
                  to="/about/"
                  className={`transition text-base tracking-wide font-bold ${
                    pathname === '/about'
                      ? 'text-[#22D3D3]'
                      : 'text-white'
                  }`}
                  onClick={handleNavClick}
                >
                  ABOUT
                </Link>
                <Link
                  to="/evals/"
                  className={`transition text-base tracking-wide font-bold ${
                    pathname === '/evals'
                      ? 'text-[#22D3D3]'
                      : 'text-white'
                  }`}
                  onClick={handleNavClick}
                >
                  EVALS
                </Link>
                <Link
                  to="/contact/"
                  className={`transition text-base tracking-wide font-bold ${
                    pathname === '/contact'
                      ? 'text-[#22D3D3]'
                      : 'text-white'
                  }`}
                  onClick={handleNavClick}
                >
                  CONTACT
                </Link>
                <a
                  href="https://blog.simplyfai.io/"
                  className="transition text-base tracking-wide font-bold text-white"
                  onClick={handleNavClick}
                >
                  INSIGHTS
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="mt-2 w-full max-w-none pl-0 pr-8 sm:mt-2.5 sm:pr-10 lg:pr-12 xl:pr-16" aria-hidden="true">
        <div className="h-px w-full" style={separatorStyle} />
      </div>
    </motion.nav>
  );
};

export default Navbar;
